import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  checkInBooking,
  getTodayVisitors,
  TodayVisitorsSummary,
} from "../../services/api/admin/orders";
import {
  subscribeToCheckIn,
  subscribeToOrders,
  CheckInEventData,
  OrderEventData,
} from "../../services/api/realtime";
import { BookingRecord } from "../../types";
import {
  QrCode,
  Search,
  CheckCircle2,
  XCircle,
  RefreshCcw,
  User,
  Calendar,
  Ticket,
  Camera,
  StopCircle,
  Video,
  X,
  Users,
  Activity,
  LogIn,
} from "lucide-react";
import jsQR from "jsqr";
import Swal from "sweetalert2";

interface CheckInGateProps {
  onCheckInSuccess?: () => void;
}

interface CheckInStats {
  totalExpectedPax: number;
  totalCheckedInPax: number;
  remainingPax: number;
  percent: number;
  recentCheckIns: BookingRecord[];
}

export const CheckInGate: React.FC<CheckInGateProps> = ({
  onCheckInSuccess,
}) => {
  const [inputCode, setInputCode] = useState("");
  const [todayStats, setTodayStats] = useState<CheckInStats>({
    totalExpectedPax: 0,
    totalCheckedInPax: 0,
    remainingPax: 0,
    percent: 0,
    recentCheckIns: [],
  });
  const [scanResult, setScanResult] = useState<{
    status: "idle" | "success" | "error";
    message: string;
    data?: BookingRecord;
  }>({ status: "idle", message: "" });
  const [isProcessing, setIsProcessing] = useState(false);

  // Camera States
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>("");
  const [cameraError, setCameraError] = useState("");

  const inputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const requestRef = useRef<number>(0);
  const streamRef = useRef<MediaStream | null>(null);

  const refreshStats = async () => {
    try {
      const result = await getTodayVisitors();
      const checkedInVisitors = result.visitors.filter((v) => v.checkInTime);
      setTodayStats({
        totalExpectedPax: result.summary.totalVisitors,
        totalCheckedInPax: result.summary.checkedIn,
        remainingPax: result.summary.notCheckedIn,
        percent:
          result.summary.totalVisitors > 0
            ? Math.round(
                (result.summary.checkedIn / result.summary.totalVisitors) * 100
              )
            : 0,
        recentCheckIns: checkedInVisitors.slice(0, 10),
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Gagal memuat statistik hari ini",
        text: "Terjadi kesalahan saat memuat statistik check-in hari ini. Silakan coba lagi.",
      });
    }
  };

  useEffect(() => {
    refreshStats();

    return () => {
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  useEffect(() => {
    const unsubscribeCheckIn = subscribeToCheckIn((data: CheckInEventData) => {
      refreshStats();
    });

    const unsubscribeOrders = subscribeToOrders(
      (data: OrderEventData) => {
        const today = new Date().toISOString().split("T")[0];
        if (data.visitDate === today) {
          refreshStats();

          const Toast = Swal.mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 4000,
            timerProgressBar: true,
          });
          Toast.fire({
            icon: "info",
            title: "Tiket Baru",
            text: `${data.customerName} - ${data.participant} pax`,
          });
        }
      },
      (data: OrderEventData) => {
        const today = new Date().toISOString().split("T")[0];
        if (data.visitDate === today) {
          refreshStats();
        }
      }
    );

    return () => {
      unsubscribeCheckIn();
      unsubscribeOrders();
    };
  }, []);

  // Auto-focus input for barcode scanners
  useEffect(() => {
    if (!isCameraOpen) {
      inputRef.current?.focus();
    }
  }, [isCameraOpen]);

  // Fetch Cameras on Mount
  useEffect(() => {
    const getDevices = async () => {
      try {
        // Detect iOS
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
                      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoInputs = devices.filter(
          (device) => device.kind === "videoinput"
        );
        setVideoDevices(videoInputs);

        if (videoInputs.length > 0) {
          if (isIOS) {
            // For iOS, just use the first available camera
            // iOS will handle facingMode in getUserMedia
            setSelectedDeviceId(videoInputs[0].deviceId);
          } else {
            // For desktop, try to find back camera
            const backCamera = videoInputs.find(
              (d) =>
                d.label.toLowerCase().includes("back") ||
                d.label.toLowerCase().includes("belakang")
            );
            setSelectedDeviceId(
              backCamera ? backCamera.deviceId : videoInputs[0].deviceId
            );
          }
        }
      } catch (err) {
        console.error('Error enumerating devices:', err);
        // Fallback: still allow camera access even if enumeration fails
        setVideoDevices([]);
        setSelectedDeviceId('default');
      }
    };
    getDevices();
  }, []);

  // Camera Logic
  const checkCameraPermission = async () => {
    try {
      // Detect iOS
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
                    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

      // iOS requires HTTPS for camera access
      if (isIOS && location.protocol !== 'https:') {
        throw new Error('iOS memerlukan HTTPS untuk akses kamera. Silakan gunakan koneksi aman (https://).');
      }
      if (!isIOS && navigator.permissions) {
        const permission = await navigator.permissions.query({ name: 'camera' as any });
        if (permission.state === 'denied') {
          throw new Error('Izin kamera ditolak. Silakan izinkan akses kamera di pengaturan browser Anda (klik ikon kunci di address bar).');
        }
      }

      // Show loading message
      const loadingToast = Swal.fire({
        title: 'Memeriksa akses kamera...',
        text: 'Mohon tunggu sebentar',
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      // Try to get user media briefly to trigger permission prompt
      let testConstraints: MediaStreamConstraints;

      if (isIOS) {
        testConstraints = {
          video: {
            facingMode: 'environment',
            width: { ideal: 640 },
            height: { ideal: 480 }
          }
        };
      } else {
        testConstraints = {
          video: {
            width: { ideal: 640 },
            height: { ideal: 480 }
          }
        };
      }

      const testStream = await navigator.mediaDevices.getUserMedia(testConstraints);
      testStream.getTracks().forEach(track => track.stop()); // Stop immediately

      // Close loading
      Swal.close();

      return true;
    } catch (err: any) {
      console.error('Camera permission error:', err);

      // Close any loading
      Swal.close();

      let errorMessage = 'Aplikasi memerlukan izin akses kamera untuk scan QR code.';

      if (isIOS) {
        errorMessage = 'Untuk iOS: Pastikan menggunakan HTTPS dan izinkan akses kamera. Coba refresh halaman jika masih bermasalah.';
      }

      if (err.name === 'NotAllowedError') {
        errorMessage = isIOS
          ? 'Izin kamera ditolak. Di iOS, pastikan Anda mengklik "Izinkan" saat diminta dan coba lagi.'
          : 'Izin kamera ditolak. Silakan klik ikon kunci di address bar browser dan izinkan akses kamera.';
      } else if (err.name === 'NotFoundError') {
        errorMessage = 'Tidak ada kamera yang ditemukan di perangkat ini.';
      } else if (err.name === 'NotReadableError') {
        errorMessage = 'Kamera sedang digunakan oleh aplikasi lain.';
      } else if (err.message) {
        errorMessage = err.message;
      }

      Swal.fire({
        icon: 'error',
        title: 'Izin Kamera Diperlukan',
        text: errorMessage,
        confirmButtonText: 'Mengerti',
        customClass: {
          popup: 'rounded-[2rem]',
          confirmButton: 'rounded-xl px-6 py-3 font-black text-[10px] uppercase tracking-widest'
        }
      });
      return false;
    }
  };

  const startCamera = useCallback(async (deviceId: string) => {
    if (!deviceId) return null;
    setCameraError("");

    // Detect iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
                  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    try {
      let constraints: MediaStreamConstraints;

      if (isIOS || deviceId === 'default') {
        // iOS prefers facingMode over deviceId, and fallback for enumeration failure
        constraints = {
          video: {
            facingMode: 'environment', // Use back camera
            width: { ideal: 640 },
            height: { ideal: 480 },
          },
        };
      } else {
        // Desktop browsers can use deviceId
        constraints = {
          video: {
            deviceId: { exact: deviceId },
            width: { ideal: 640 },
            height: { ideal: 480 },
          },
        };
      }

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      return stream;
    } catch (err: any) {
      console.error("Camera Error", err);

      let errorMessage = "Gagal mengakses kamera.";
      if (err.name === 'NotAllowedError') {
        errorMessage = "Izin kamera ditolak. Silakan izinkan akses kamera.";
      } else if (err.name === 'NotFoundError') {
        errorMessage = "Kamera tidak ditemukan.";
      } else if (err.name === 'NotReadableError') {
        errorMessage = "Kamera sedang digunakan aplikasi lain.";
      } else if (err.name === 'OverconstrainedError') {
        // Fallback for iOS if facingMode fails
        errorMessage = "Kamera tidak kompatibel. Coba gunakan kamera belakang.";
      }

      setCameraError(errorMessage);
      setIsCameraOpen(false);
      return null;
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (requestRef.current) {
      cancelAnimationFrame(requestRef.current);
      requestRef.current = 0;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        track.stop();
      });
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.pause();
      if (videoRef.current.srcObject) {
        const stream = videoRef.current.srcObject as MediaStream;
        stream.getTracks().forEach((track) => {
          track.stop();
        });
      }
      videoRef.current.srcObject = null;
    }
  }, []);

  useEffect(() => {
    // Detect iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
                  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    if (!isCameraOpen || !selectedDeviceId) {
      // For iOS, don't stop camera if we just opened it directly
      if (!isIOS || !streamRef.current) {
        stopCamera();
      }
      return;
    }

    // For iOS, camera is already initialized directly in the click handler
    if (isIOS) {
      return;
    }

    let isActive = true;

    const initCamera = async () => {
      stopCamera();

      const stream = await startCamera(selectedDeviceId);

      if (!isActive || !stream) {
        if (stream) {
          stream.getTracks().forEach((track) => track.stop());
        }
        return;
      }

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          if (isActive && videoRef.current) {
            videoRef.current.play();
            requestRef.current = requestAnimationFrame(scanTick);
          }
        };
      }
    };

    initCamera();

    return () => {
      isActive = false;
      stopCamera();
    };
  }, [isCameraOpen, selectedDeviceId, startCamera, stopCamera]);

  const scanTick = () => {
    if (
      videoRef.current &&
      videoRef.current.readyState === videoRef.current.HAVE_ENOUGH_DATA
    ) {
      const canvas = canvasRef.current;
      const video = videoRef.current;
      if (canvas) {
        canvas.height = video.videoHeight;
        canvas.width = video.videoWidth;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        if (ctx) {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const code = jsQR(imageData.data, imageData.width, imageData.height, {
            inversionAttempts: "dontInvert",
          });
          if (code && code.data) {
            processCode(code.data);
            return;
          }
        }
      }
    }
    requestRef.current = requestAnimationFrame(scanTick);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const processCode = async (
    codeRaw: string,
    confirmPayment: boolean = false
  ) => {
    if (isProcessing) return;

    const cleanCode = codeRaw.replace("GIZPARK-", "").trim();
    setInputCode(cleanCode);
    setIsCameraOpen(false);
    setIsProcessing(true);

    try {
      const result = await checkInBooking(cleanCode, confirmPayment);

      if (result.requirePaymentConfirmation && result.data) {
        const paymentData = result.data;
        const swalResult = await Swal.fire({
          title:
            '<span class="text-amber-600 font-black uppercase tracking-tight">Konfirmasi Pembayaran</span>',
          html: `
                        <div class="text-left space-y-4 p-4">
                            <div class="bg-amber-50 border-2 border-amber-200 rounded-2xl p-4">
                                <p class="text-[10px] font-black text-amber-600 uppercase tracking-widest mb-2">⚠️ Tiket Belum Dibayar</p>
                                <p class="text-sm text-amber-700">Pembayaran di loket diperlukan sebelum check-in.</p>
                            </div>
                            <div class="bg-slate-50 rounded-2xl p-4 space-y-3">
                                <div class="flex justify-between items-center border-b border-slate-200 pb-2">
                                    <span class="text-[10px] font-bold text-slate-400 uppercase">Kode Booking</span>
                                    <span class="font-black text-slate-900">${
                                      paymentData.bookingCode
                                    }</span>
                                </div>
                                <div class="flex justify-between items-center border-b border-slate-200 pb-2">
                                    <span class="text-[10px] font-bold text-slate-400 uppercase">Nama</span>
                                    <span class="font-bold text-slate-900">${
                                      paymentData.customer
                                    }</span>
                                </div>
                                <div class="flex justify-between items-center border-b border-slate-200 pb-2">
                                    <span class="text-[10px] font-bold text-slate-400 uppercase">No. HP</span>
                                    <span class="font-bold text-slate-900">${
                                      paymentData.phone || "-"
                                    }</span>
                                </div>
                                <div class="flex justify-between items-center border-b border-slate-200 pb-2">
                                    <span class="text-[10px] font-bold text-slate-400 uppercase">Tiket</span>
                                    <span class="font-bold text-slate-900">${
                                      paymentData.ticket
                                    }</span>
                                </div>
                                <div class="flex justify-between items-center border-b border-slate-200 pb-2">
                                    <span class="text-[10px] font-bold text-slate-400 uppercase">Jumlah</span>
                                    <span class="font-bold text-slate-900">${
                                      paymentData.participant
                                    } Pax</span>
                                </div>
                                <div class="flex justify-between items-center border-b border-slate-200 pb-2">
                                    <span class="text-[10px] font-bold text-slate-400 uppercase">Tgl Kunjungan</span>
                                    <span class="font-bold text-slate-900">${
                                      paymentData.visitDate
                                    }</span>
                                </div>
                            </div>
                            <div class="bg-indigo-600 text-white rounded-2xl p-4 text-center">
                                <p class="text-[10px] font-bold uppercase tracking-widest mb-1 opacity-80">Total yang Harus Dibayar</p>
                                <p class="text-2xl font-black">${formatCurrency(
                                  paymentData.totalAmount || 0
                                )}</p>
                            </div>
                        </div>
                    `,
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#10B981",
          cancelButtonColor: "#EF4444",
          confirmButtonText: "✓ Sudah Bayar, Proses Check-In",
          cancelButtonText: "✕ Belum Bayar",
          customClass: {
            popup: "rounded-[2.5rem]",
            confirmButton:
              "rounded-xl px-6 py-3 font-black text-[10px] uppercase tracking-widest",
            cancelButton:
              "rounded-xl px-6 py-3 font-black text-[10px] uppercase tracking-widest",
          },
        });

        if (swalResult.isConfirmed) {
          setIsProcessing(false);
          await processCode(cleanCode, true);
          return;
        } else {
          setScanResult({
            status: "error",
            message: "Pembayaran belum dikonfirmasi",
          });
        }
      } else if (result.success) {
        setScanResult({
          status: "success",
          message: result.message,
          data: result.data
            ? {
                id: "",
                code: result.data.bookingCode,
                customerName: result.data.customer,
                email: "",
                phone: result.data.phone || "",
                date: "",
                category: "Personal",
                ticketId: "",
                ticketType: result.data.ticket,
                pax: result.data.participant,
                totalPrice: result.data.totalAmount || 0,
                status: "COMPLETED",
                paymentMethod: "QRIS",
                bookingDate: "",
                checkInTime: result.data.checkInAt,
              }
            : undefined,
        });
        await refreshStats();
        if (onCheckInSuccess) onCheckInSuccess();

        Swal.fire({
          title:
            '<span class="text-emerald-600 font-black uppercase tracking-tight">Check-In Berhasil!</span>',
          html: `<p class="text-slate-600">${result.data?.customer} - ${result.data?.participant} Pax</p>`,
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
          customClass: {
            popup: "rounded-[2rem]",
          },
        });
      } else {
        setScanResult({
          status: "error",
          message: result.message,
        });
      }
    } catch (error: any) {
      setScanResult({
        status: "error",
        message: error?.data?.message || "Gagal melakukan check-in. Coba lagi.",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleManualSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputCode.trim()) return;
    processCode(inputCode);
    setInputCode("");
  };

  const reset = () => {
    setScanResult({ status: "idle", message: "" });
    setInputCode("");
    inputRef.current?.focus();
  };

  return (
    <div className="max-w-5xl mx-auto animate-fadeIn space-y-6">
      {/* Live Statistics Dashboard at Top */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="md:col-span-1 bg-white p-5 rounded-2xl border border-gray-200 shadow-sm flex flex-col justify-center">
          <p className="text-gray-400 text-[10px] font-bold uppercase tracking-wider mb-2 flex items-center gap-1">
            <Activity className="w-3 h-3 text-indigo-500" /> Okupansi Gate
          </p>
          <div className="flex items-center gap-3">
            <div className="text-3xl font-black text-gray-900">
              {todayStats.percent}%
            </div>
            <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="bg-indigo-600 h-full rounded-full transition-all duration-1000"
                style={{ width: `${todayStats.percent}%` }}
              ></div>
            </div>
          </div>
        </div>

        <div className="bg-indigo-600 text-white p-5 rounded-2xl shadow-lg relative overflow-hidden">
          <div className="relative z-10">
            <p className="text-indigo-100 text-[10px] font-bold uppercase tracking-wider mb-1">
              Tiket Booking
            </p>
            <h3 className="text-3xl font-black">
              {todayStats.totalExpectedPax}{" "}
              <span className="text-sm font-normal opacity-70">Pax</span>
            </h3>
          </div>
          <Users className="absolute -bottom-2 -right-2 w-16 h-16 opacity-10" />
        </div>

        <div className="bg-emerald-600 text-white p-5 rounded-2xl shadow-lg relative overflow-hidden">
          <div className="relative z-10">
            <p className="text-emerald-100 text-[10px] font-bold uppercase tracking-wider mb-1">
              Sudah Masuk
            </p>
            <h3 className="text-3xl font-black">
              {todayStats.totalCheckedInPax}{" "}
              <span className="text-sm font-normal opacity-70">Pax</span>
            </h3>
          </div>
          <LogIn className="absolute -bottom-2 -right-2 w-16 h-16 opacity-10" />
        </div>

        <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm relative overflow-hidden">
          <p className="text-gray-400 text-[10px] font-bold uppercase tracking-wider mb-1">
            Sisa Antrean
          </p>
          <h3 className="text-3xl font-black text-gray-900">
            {todayStats.remainingPax}{" "}
            <span className="text-sm font-normal text-gray-400">Pax</span>
          </h3>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Check-In Action Area */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-3xl shadow-xl border border-gray-200 overflow-hidden">
            {isCameraOpen ? (
              <div className="relative bg-black">
                <video
                  ref={videoRef}
                  className="w-full h-64 md:h-[400px] object-cover"
                  playsInline
                  muted
                ></video>
                <canvas ref={canvasRef} className="hidden"></canvas>
                <div className="absolute inset-0 flex flex-col justify-between p-4">
                  <div className="flex justify-between items-center bg-black/40 backdrop-blur-sm p-2 rounded-xl text-white">
                    <div className="flex items-center gap-2">
                      <Video className="w-4 h-4" />
                      <select
                        value={selectedDeviceId}
                        onChange={(e) => setSelectedDeviceId(e.target.value)}
                        className="bg-transparent border-none text-xs font-bold focus:ring-0 outline-none max-w-[150px] truncate"
                      >
                        {videoDevices.map((device) => (
                          <option
                            key={device.deviceId}
                            value={device.deviceId}
                            className="text-black"
                          >
                            {device.label ||
                              `Camera ${device.deviceId.slice(0, 5)}...`}
                          </option>
                        ))}
                      </select>
                    </div>
                    <button
                      onClick={() => setIsCameraOpen(false)}
                      className="bg-white/20 p-1.5 rounded-full hover:bg-white/40"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 border-2 border-white/50 rounded-xl flex items-center justify-center">
                    <div className="w-40 h-40 border-2 border-indigo-400 rounded-lg animate-pulse relative">
                      <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-indigo-400 -mt-1 -ml-1"></div>
                      <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-indigo-400 -mt-1 -mr-1"></div>
                      <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-indigo-400 -mb-1 -ml-1"></div>
                      <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-indigo-400 -mb-1 -mr-1"></div>
                    </div>
                  </div>
                  <div className="text-center">
                    <p className="text-white/80 text-[10px] font-bold uppercase tracking-widest bg-black/40 px-3 py-1 rounded-full inline-block backdrop-blur-sm">
                      Arahkan kamera ke QR Code Tiket
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-8">
                <form onSubmit={handleManualSubmit} className="relative">
                  <QrCode className="absolute left-4 top-1/2 -translate-y-1/2 w-8 h-8 text-gray-300" />
                  <input
                    ref={inputRef}
                    type="text"
                    value={inputCode}
                    onChange={(e) => setInputCode(e.target.value)}
                    className="w-full pl-16 pr-4 py-6 text-2xl font-mono font-bold border-2 border-gray-100 rounded-2xl focus:border-indigo-600 focus:ring-4 focus:ring-indigo-100 outline-none transition-all placeholder:text-gray-300 placeholder:font-sans uppercase"
                    placeholder="SCAN ATAU KETIK KODE..."
                  />
                  <button
                    type="submit"
                    className="absolute right-3 top-3 bottom-3 bg-gray-900 text-white px-6 rounded-xl font-bold hover:bg-black transition-colors"
                  >
                    Check
                  </button>
                </form>
                <div className="mt-6 flex justify-center">
                  <button
                    onClick={async () => {
                      // Detect iOS
                      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
                                    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

                      if (isIOS) {
                        // For iOS, directly call getUserMedia from user gesture
                        setCameraError("");
                        try {
                          const stream = await navigator.mediaDevices.getUserMedia({
                            video: {
                              facingMode: 'environment',
                              width: { ideal: 640 },
                              height: { ideal: 480 },
                            },
                          });

                          // If successful, set camera open and assign stream
                          streamRef.current = stream;
                          if (videoRef.current) {
                            videoRef.current.srcObject = stream;
                            videoRef.current.onloadedmetadata = () => {
                              if (videoRef.current) {
                                videoRef.current.play();
                                requestRef.current = requestAnimationFrame(scanTick);
                              }
                            };
                          }
                          setIsCameraOpen(true);
                        } catch (err: any) {
                          console.error("iOS Camera Error", err);
                          let errorMessage = "Gagal mengakses kamera.";
                          if (err.name === 'NotAllowedError') {
                            errorMessage = "Izin kamera ditolak. Di iOS, pastikan Anda mengklik 'Izinkan' saat diminta.";
                          } else if (err.name === 'NotFoundError') {
                            errorMessage = "Kamera tidak ditemukan.";
                          } else if (err.name === 'NotReadableError') {
                            errorMessage = "Kamera sedang digunakan aplikasi lain.";
                          }
                          setCameraError(errorMessage);
                        }
                      } else {
                        // For desktop, use the existing permission check flow
                        const hasPermission = await checkCameraPermission();
                        if (hasPermission) {
                          setIsCameraOpen(true);
                        }
                      }
                    }}
                    className="flex items-center gap-2 text-indigo-600 bg-indigo-50 px-6 py-3 rounded-xl font-bold hover:bg-indigo-100 transition-colors"
                  >
                    <Camera className="w-5 h-5" /> Gunakan Kamera Perangkat
                  </button>
                </div>
                {cameraError && (
                  <p className="text-center text-red-500 text-xs mt-3 font-bold uppercase tracking-wide">
                    {cameraError}
                  </p>
                )}
              </div>
            )}
          </div>

          {scanResult.status !== "idle" && (
            <div
              className={`rounded-3xl p-8 border-2 shadow-xl animate-scaleIn text-center relative overflow-hidden ${
                scanResult.status === "success"
                  ? "bg-emerald-50 border-emerald-100"
                  : "bg-red-50 border-red-100"
              }`}
            >
              <div className="relative z-10">
                <div
                  className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${
                    scanResult.status === "success"
                      ? "bg-emerald-100 text-emerald-600"
                      : "bg-red-100 text-red-600"
                  }`}
                >
                  {scanResult.status === "success" ? (
                    <CheckCircle2 className="w-10 h-10" />
                  ) : (
                    <XCircle className="w-10 h-10" />
                  )}
                </div>
                <h3
                  className={`text-2xl font-black mb-2 ${
                    scanResult.status === "success"
                      ? "text-emerald-900"
                      : "text-red-900"
                  }`}
                >
                  {scanResult.message}
                </h3>
                {scanResult.data && (
                  <div className="mt-6 bg-white/80 backdrop-blur-sm rounded-2xl p-6 text-left max-w-lg mx-auto shadow-sm border border-white">
                    <div className="grid grid-cols-2 gap-y-4 gap-x-6 text-sm">
                      <div className="space-y-1">
                        <p className="text-gray-400 text-[10px] font-bold uppercase">
                          Nama Pengunjung
                        </p>
                        <p className="font-bold text-gray-900 truncate">
                          {scanResult.data.customerName}
                        </p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-gray-400 text-[10px] font-bold uppercase">
                          Tipe Tiket
                        </p>
                        <p className="font-bold text-gray-900">
                          {scanResult.data.ticketType}
                        </p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-gray-400 text-[10px] font-bold uppercase">
                          Jumlah Pax
                        </p>
                        <p className="font-bold text-indigo-600 text-xl">
                          {scanResult.data.pax} Orang
                        </p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-gray-400 text-[10px] font-bold uppercase">
                          ID Booking
                        </p>
                        <p className="font-mono font-bold text-gray-900">
                          {scanResult.data.code}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                <button
                  onClick={reset}
                  className="mt-8 flex items-center gap-2 mx-auto text-gray-500 font-bold hover:text-gray-800 bg-white px-6 py-2.5 rounded-full shadow-md border border-gray-100 transition-all active:scale-95"
                >
                  <RefreshCcw className="w-4 h-4" /> Reset / Scan Berikutnya
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Recent Check-Ins Sidebar */}
        <div className="space-y-4">
          <div className="bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden h-full">
            <div className="p-5 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
              <h4 className="font-black text-gray-900 text-sm uppercase tracking-widest">
                Aktivitas Terkini
              </h4>
              <span className="bg-emerald-100 text-emerald-700 text-[10px] px-2 py-0.5 rounded font-bold">
                LIVE
              </span>
            </div>
            <div className="p-0">
              {todayStats.recentCheckIns &&
              todayStats.recentCheckIns.length > 0 ? (
                <div className="divide-y divide-gray-50">
                  {todayStats.recentCheckIns.map((checkIn, i) => (
                    <div
                      key={i}
                      className="p-4 hover:bg-gray-50 transition-colors flex justify-between items-center"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-gray-900 truncate text-sm">
                          {checkIn.customerName}
                        </p>
                        <p className="text-[10px] text-gray-500 mt-0.5">
                          {checkIn.ticketType} • {checkIn.pax} Pax
                        </p>
                      </div>
                      <div className="text-right ml-3">
                        <p className="text-[10px] font-mono font-bold text-indigo-600">
                          {checkIn.checkInTime
                            ? new Date(checkIn.checkInTime).toLocaleTimeString(
                                "id-ID",
                                { hour: "2-digit", minute: "2-digit" }
                              )
                            : "--:--"}
                        </p>
                        <p className="text-[10px] text-gray-400">WIB</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-10 text-center">
                  <LogIn className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                  <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">
                    Belum ada scan masuk
                  </p>
                </div>
              )}
            </div>
            <div className="p-4 bg-gray-50 border-t border-gray-100">
              <p className="text-[10px] text-center text-gray-500 leading-relaxed">
                Pastikan koneksi stabil untuk sinkronisasi data *real-time* ke
                sistem admin pusat.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
